/**
 * Timeline row types and structural sharing (ticket 3525, upstream pin
 * e3e7cc3f, MIT), ported from upstream `MessagesTimeline.logic.ts`.
 *
 * Forge deltas: admission owns a worktree-setup row, while queued messages
 * remain on their own surface. The duration boundary reads the optional
 * `updatedAt` with a `completedAt` fallback so forge messages keep working.
 *
 * @module MessagesTimeline.rowTypes
 */

import * as Equal from "effect/Equal";
import type { OrchestrationLatestTurn, TurnId } from "@cmmd-forge/contracts";
import type { TimelineEntry, WorkLogEntry } from "../../session-logic";
import type { ChatMessage, ProposedPlan, TurnDiffSummary } from "../../types";
import type { ToolGroupSummaryKind } from "../../workLogToolGroups";
import type { WorktreeLaunchProgress } from "./worktreeLaunchProgress";

export type TimelineLatestTurn = Pick<
  OrchestrationLatestTurn,
  "turnId" | "state" | "startedAt" | "completedAt"
>;

export const LIVE_ACTIVITY_ROW_ID = "live-activity-row";

export type ActivityEntry = Extract<TimelineEntry, { kind: "message" | "work" }>;

export type MessagesTimelineRow =
  | {
      kind: "worktree-setup";
      id: string;
      createdAt: string;
      progress: WorktreeLaunchProgress;
      onCancel: () => void;
    }
  | {
      kind: "activity-group";
      id: string;
      createdAt: string;
      turnId: TurnId;
      groupId: string;
      entries: ActivityEntry[];
      expanded: boolean;
      active: boolean;
    }
  | {
      kind: "work";
      id: string;
      createdAt: string;
      groupedEntries: WorkLogEntry[];
      isExpandedToolGroup: boolean;
      displayLabel?: string;
    }
  | {
      kind: "work-live";
      id: string;
      createdAt: string;
      entry: WorkLogEntry;
      groupedEntries: WorkLogEntry[];
      groupId: string;
      expanded: boolean;
      active: boolean;
    }
  | {
      kind: "work-toggle";
      id: string;
      createdAt: string;
      turnId?: TurnId | null;
      groupId: string;
      hiddenCount: number;
      expanded: boolean;
      summary: string;
      summaryKind: ToolGroupSummaryKind;
      toolSurface?: WorkLogEntry["toolSurface"];
      toolIcon?: WorkLogEntry["toolIcon"];
      summaryToolIcon?: "browser" | "device" | "t3-code" | "pull-request";
      hasFailure: boolean;
    }
  | {
      kind: "turn-fold";
      id: string;
      createdAt: string;
      turnId: TurnId;
      label: string;
      expanded: boolean;
    }
  | {
      kind: "context-compaction";
      id: string;
      createdAt: string;
      label: string;
    }
  | {
      kind: "message";
      id: string;
      createdAt: string;
      message: ChatMessage;
      durationStart: string;
      showAssistantMeta: boolean;
      showAssistantCopyButton: boolean;
      assistantCopyStreaming: boolean;
      assistantTurnDiffSummary?: TurnDiffSummary | undefined;
      revertTurnCount?: number | undefined;
    }
  | {
      kind: "assistant-meta";
      id: string;
      createdAt: string;
      message: ChatMessage;
      showAssistantCopyButton: boolean;
      assistantCopyStreaming: boolean;
    }
  | {
      kind: "proposed-plan";
      id: string;
      createdAt: string;
      proposedPlan: ProposedPlan;
    }
  | {
      kind: "working";
      id: string;
      createdAt: string | null;
    }
  | {
      kind: "thinking";
      id: string;
      createdAt: string | null;
    };

export interface StableMessagesTimelineRowsState {
  byId: Map<string, MessagesTimelineRow>;
  result: MessagesTimelineRow[];
}

export function computeStableMessagesTimelineRows(
  rows: MessagesTimelineRow[],
  previous: StableMessagesTimelineRowsState,
): StableMessagesTimelineRowsState {
  const next = new Map<string, MessagesTimelineRow>();
  let anyChanged = rows.length !== previous.byId.size;

  const result = rows.map((row, index) => {
    const prevRow = previous.byId.get(row.id);
    const nextRow = prevRow && isRowUnchanged(prevRow, row) ? prevRow : row;
    next.set(row.id, nextRow);
    if (!anyChanged && previous.result[index] !== nextRow) {
      anyChanged = true;
    }
    return nextRow;
  });

  return anyChanged ? { byId: next, result } : previous;
}

/** Shallow field comparison per row variant — avoids deep equality cost. */
// fallow-ignore-next-line complexity -- port of upstream MessagesTimeline.logic.ts at pin e3e7cc3f; refactoring would break the provenance pin (ticket 3525)
function isRowUnchanged(a: MessagesTimelineRow, b: MessagesTimelineRow): boolean {
  if (a.kind !== b.kind || a.id !== b.id) return false;

  switch (a.kind) {
    case "worktree-setup": {
      const bw = b as typeof a;
      return (
        a.createdAt === bw.createdAt && a.progress === bw.progress && a.onCancel === bw.onCancel
      );
    }
    case "activity-group": {
      const group = b as typeof a;
      return (
        a.active === group.active &&
        a.expanded === group.expanded &&
        a.groupId === group.groupId &&
        a.entries.length === group.entries.length &&
        a.entries.every((entry, index) => entry === group.entries[index])
      );
    }
    case "working":
    case "thinking":
      return a.createdAt === (b as typeof a).createdAt;

    case "assistant-meta": {
      const bm = b as typeof a;
      return (
        a.createdAt === bm.createdAt &&
        a.message === bm.message &&
        a.showAssistantCopyButton === bm.showAssistantCopyButton &&
        a.assistantCopyStreaming === bm.assistantCopyStreaming
      );
    }

    case "turn-fold": {
      const bf = b as typeof a;
      return a.createdAt === bf.createdAt && a.label === bf.label && a.expanded === bf.expanded;
    }

    case "context-compaction": {
      const bc = b as typeof a;
      return a.createdAt === bc.createdAt && a.label === bc.label;
    }

    case "proposed-plan":
      return a.proposedPlan === (b as typeof a).proposedPlan;

    case "work": {
      const bw = b as typeof a;
      return (
        a.isExpandedToolGroup === bw.isExpandedToolGroup &&
        a.displayLabel === bw.displayLabel &&
        Equal.equals(a.groupedEntries, bw.groupedEntries)
      );
    }

    case "work-live": {
      const bw = b as typeof a;
      return (
        a.createdAt === bw.createdAt &&
        a.groupId === bw.groupId &&
        a.expanded === bw.expanded &&
        a.active === bw.active &&
        Equal.equals(a.entry, bw.entry) &&
        Equal.equals(a.groupedEntries, bw.groupedEntries)
      );
    }

    case "work-toggle": {
      const bw = b as typeof a;
      return (
        a.createdAt === bw.createdAt &&
        a.turnId === bw.turnId &&
        a.groupId === bw.groupId &&
        a.hiddenCount === bw.hiddenCount &&
        a.expanded === bw.expanded &&
        a.summary === bw.summary &&
        a.summaryKind === bw.summaryKind &&
        a.toolSurface === bw.toolSurface &&
        Equal.equals(a.toolIcon, bw.toolIcon) &&
        a.hasFailure === bw.hasFailure
      );
    }

    case "message": {
      const bm = b as typeof a;
      return (
        a.message === bm.message &&
        a.durationStart === bm.durationStart &&
        a.showAssistantMeta === bm.showAssistantMeta &&
        a.showAssistantCopyButton === bm.showAssistantCopyButton &&
        a.assistantCopyStreaming === bm.assistantCopyStreaming &&
        a.assistantTurnDiffSummary === bm.assistantTurnDiffSummary &&
        a.revertTurnCount === bm.revertTurnCount
      );
    }
  }
}

export interface TimelineDurationMessage {
  id: string;
  role: ChatMessage["role"];
  createdAt: string;
  updatedAt?: string | undefined;
  completedAt?: string | undefined;
  streaming: boolean;
}

/** The message timestamp that closes a duration window, forge messages included. */
export function messageEndTime(message: {
  updatedAt?: string | undefined;
  completedAt?: string | undefined;
}): string | undefined {
  return message.updatedAt ?? message.completedAt;
}

export function computeMessageDurationStart(
  messages: ReadonlyArray<TimelineDurationMessage>,
): Map<string, string> {
  const result = new Map<string, string>();
  let lastBoundary: string | null = null;

  for (const message of messages) {
    if (message.role === "user") {
      lastBoundary = message.createdAt;
    }
    result.set(message.id, lastBoundary ?? message.createdAt);
    if (message.role === "assistant" && !message.streaming) {
      lastBoundary = messageEndTime(message) ?? message.createdAt;
    }
  }

  return result;
}
